import type { QubicKind } from "api";
import cn from "classnames";

export type KindTab = {
  kind: QubicKind;
  title: string;
  text: string;
  /** My Qubics shows how many it holds; left out where there is no count to give. */
  count?: number;
};

type KindTabsProps = {
  tabs: KindTab[];
  active: QubicKind;
  onSelect: (kind: QubicKind) => void;
  /** Off until the counts are known, so a loading list never reads as zero. */
  showCounts?: boolean;
};

const KindIcon = ({ kind }: { kind: QubicKind }) =>
  kind === "api" ? (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 8l-4 4 4 4" />
      <path d="M15 8l4 4-4 4" />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  );

/** The two-way `.st-kinds` switch the Store and My Qubics both open with. */
export const KindTabs = ({ tabs, active, onSelect, showCounts = true }: KindTabsProps) => (
  <div className="st-kinds" role="tablist">
    {tabs.map((tab) => {
      const on = tab.kind === active;

      return (
        <button
          key={tab.kind}
          type="button"
          className={cn("st-kind", { "is-on": on })}
          role="tab"
          aria-selected={on}
          onClick={() => onSelect(tab.kind)}
        >
          <span className="st-kind__i">
            <KindIcon kind={tab.kind} />
          </span>
          <span className="st-kind__t">
            <b>{tab.title}</b>
            <em>{tab.text}</em>
          </span>
          {showCounts && tab.count !== undefined && (
            <span className={cn("st-kind__c", { "st-kind__c--0": tab.count === 0 })}>{tab.count}</span>
          )}
        </button>
      );
    })}
  </div>
);
